import {Component, Input, OnInit} from '@angular/core';
import {CalendarEvent} from "angular-calendar";
import {MatDialog} from "@angular/material/dialog";
import {EventPlannerDialogComponent} from "../event-planner-dialog/event-planner-dialog.component";
import {ESMTriggerRule} from "../../../../models/trigger";
import {TimingRulesService} from "../../../services/timing-rules/timing-rules.service";
import {InterventionContentItem} from "../../../../models/intervention-content-item";
import {
  CanEditTimingRulesDialogComponent
} from "../can-edit-timing-rules-dialog/can-edit-timing-rules-dialog.component";

@Component({
  selector: 'dashboard-esm-calender',
  templateUrl: './esm-calender.component.html',
  styleUrls: ['./esm-calender.component.scss']
})
export class ESMCalenderComponent implements OnInit {
  @Input() moduleTitle: string;
  @Input() contentItem: InterventionContentItem;
  @Input() triggerType: string;
  @Input() esmTriggerRules: ESMTriggerRule[] = [];
  @Input() esmModuleNames: any[];
  @Input() mode: string;
  viewDate: Date = new Date();
  events: CalendarEvent[] = [];

  monthView = true;
  weekView = false;

  day: any;
  showMoreEvents = false;
  idCount: number

  isRelativeTriggerType = false
  participantStartDay = '';

  loadEventDetails = false;

  constructor(private dialog: MatDialog, private timingRulesService: TimingRulesService) {
    if (localStorage.getItem('calenderView')) {
      const view = localStorage.getItem('calenderView');
      this.monthView = view === 'month';
      this.weekView = view === 'week';
    }
  }

  ngOnInit(): void {
    if (this.triggerType === 'esm_relative_date') {
      this.isRelativeTriggerType = true;
      if (!this.participantStartDay) {
        this.participantStartDay = this.viewDate.toISOString().split('T')[0];
      }
    }

    if (this.mode === 'overview') {
      this.monthView = true;
      this.weekView = false;
      if (!this.participantStartDay) {
        this.participantStartDay = this.viewDate.toISOString().split('T')[0];
      }
    }

    this.initESMRules();
  }

  initESMRules() {
    if (this.esmTriggerRules) {
      let triggerId = 0;
      const colors = [
        '#46AE9B', '#FFC107', '#FF5722', '#673AB7', '#2196F3', '#4CAF50', '#FF9800', '#9C27B0',
        '#03A9F4', '#E91E63', '#00BCD4', '#8BC34A', '#CDDC39', '#FFEB3B', '#795548', '#607D8B',
        '#F44336', '#3F51B5', '#00C853', '#D500F9', '#1B5E20', '#FF6F00', '#004D40', '#FF3D00',
        '#BF360C', '#1A237E', '#AEEA00', '#2962FF', '#304FFE', '#6200EA', '#AA00FF', '#C51162',
        '#C2185B', '#E040FB', '#6D4C41', '#388E3C', '#7B1FA2', '#0288D1', '#0277BD', '#01579B',
        '#B0BEC5', '#FFD600', '#F50057', '#009688', '#6A1B9A', '#FF9100', '#43A047', '#673AB7'
      ];

      this.esmTriggerRules.sort((a, b) => a.triggerId - b.triggerId);

      this.esmTriggerRules.forEach((rule, index) => {

        triggerId = rule.triggerId;
        let dateParts: number[];
        let startTimeParts: number[];
        let endTimeParts: number[];

        if (rule.triggerType === 'esm_fixed_date') {
          dateParts = rule.fixedDate.split('-').map(part => parseInt(part, 10));
          startTimeParts = rule.startTime.split(':').map(part => parseInt(part, 10));
          endTimeParts = rule.endTime.split(':').map(part => parseInt(part, 10));
        } else {
          dateParts = this.offsetToDate(rule.daysOffset).split('-').map(part => parseInt(part, 10));
          startTimeParts = rule.startTime.split(':').map(part => parseInt(part, 10));
          endTimeParts = rule.endTime.split(':').map(part => parseInt(part, 10));
        }

        const newEvent: CalendarEvent = {
          id: rule.id ? rule.id : rule.eventId,
          start: new Date(dateParts[0], dateParts[1] - 1, dateParts[2], startTimeParts[0], startTimeParts[1]),
          end: new Date(dateParts[0], dateParts[1] - 1, dateParts[2], endTimeParts[0], endTimeParts[1]),
          title: this.moduleTitle ? this.moduleTitle : this.esmModuleNames.find(line => line.id === triggerId).name,
          color: {
            primary: this.mode === 'overview' ? colors[this.esmModuleNames.findIndex(line => line.id === triggerId)] : '#46AE9B',
            secondary: this.mode === 'overview' ? colors[this.esmModuleNames.findIndex(line => line.id === triggerId)] : '#46AE9B',
          },
        };
        this.events = [...this.events, newEvent];
      });
    }
  }

  changeCalendarView(view: string) {
    this.monthView = view === 'month';
    this.weekView = view === 'week';

    localStorage.setItem('calenderView', view);
  }

  clearCalender() {
    this.timingRulesService.esmTriggerEditable(
      this.contentItem?.module?.studyId,
      this.contentItem?.interventionId,
      this.contentItem?.module?.id).subscribe(result => {
        if (result) {
          this.events = [];
          this.esmTriggerRules = [];
        } else {
          this.dialog.open(CanEditTimingRulesDialogComponent, {
            width: '250px',
          });
        }
    });
  }

  navigateCalendar(direction: number) {
    if (this.monthView) {
      this.viewDate = new Date(
        this.viewDate.getFullYear(),
        this.viewDate.getMonth() + direction,
        1
      );
    } else if (this.weekView) {
      const newDate = new Date(this.viewDate);
      newDate.setDate(this.viewDate.getDate() + direction * 7);
      this.viewDate = newDate;
    }
  }

  getMonthDisplay() {
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return `${monthNames[this.viewDate.getMonth()]} ${this.viewDate.getFullYear()}`;
  }

  openPlannerDialog(dayClickedEvent: { date: Date }, view: string) {
    if (this.mode === 'overview') {
      return;
    }
    if (view === 'week') {
      const dialogRef = this.dialog.open(EventPlannerDialogComponent, {
        data: {
          participantStartDay: this.participantStartDay,
          relativeType: this.isRelativeTriggerType,
          moduleTitle: this.moduleTitle,
          view,
          date: dayClickedEvent
        },
        width: '600px',
        panelClass: 'dashboard-event-planner-dialog',
      });

      dialogRef.afterClosed().subscribe(data => {
        if (data) {
          if (data.listOfEvents) {
            data.listOfEvents.forEach(eventData => {
              this.createEvent(eventData);
            });
          } else {
            this.createEvent(data);
          }
        }
      });
    } else {
      const dialogRef = this.dialog.open(EventPlannerDialogComponent, {
        data: {
          participantStartDay: this.participantStartDay,
          relativeType: this.isRelativeTriggerType,
          moduleTitle: this.moduleTitle,
          view,
          date: dayClickedEvent.date
        },
        width: '600px',
        panelClass: 'dashboard-event-planner-dialog',
      });

      dialogRef.afterClosed().subscribe(data => {
        if (data) {
          if (data.listOfEvents) {
            data.listOfEvents.forEach(eventData => {
              this.createEvent(eventData);
            });
          } else {
            this.createEvent(data);
          }
        }
      });
    }
  }

  openEventPlannerDialog(event: any) {
    if (this.mode === 'overview') {
      return;
    }

    if (!this.loadEventDetails) {
      this.loadEventDetails = true;
    } else {
      return;
    }

    this.timingRulesService.esmTriggerEditable(
      this.contentItem?.module?.studyId,
      this.contentItem?.interventionId,
      this.contentItem?.module?.id).subscribe(result => {
      this.loadEventDetails = false;
      if (result) {
        const rule: ESMTriggerRule = this.getESMTriggerRuleById(event.id);
        const dialogRef = this.dialog.open(EventPlannerDialogComponent, {
          data: {
            participantStartDay: this.participantStartDay,
            relativeType: this.isRelativeTriggerType,
            id: rule.id ? rule.id : rule.eventId,
            moduleTitle: event.title,
            date: rule.fixedDate,
            daysOffset: rule.daysOffset,
            start: rule.startTime,
            end: rule.endTime,
            optionType: rule.optionType,
            amount: rule.amount,
            setTimes: rule.times
          },
          width: '600px',
          panelClass: 'dashboard-event-planner-dialog',
        });

        dialogRef.afterClosed().subscribe(data => {
          if (data === undefined) {
            return;
          }

          if (data.remove) {
            this.removeEvent(data.id);
            return;
          }

          this.updateEvent(data);
        });
      } else {
        this.dialog.open(CanEditTimingRulesDialogComponent, {
          width: '250px',
        });
      }
    });
  }

  getESMTriggerRuleById(id: number): ESMTriggerRule {
    return this.esmTriggerRules.find(rule => {
      const tempId = rule.id ? rule.id : rule.eventId;
      return tempId === id
    });
  }

  toggleShowMore(day: any) {
    this.showMoreEvents = !this.showMoreEvents
    if (this.showMoreEvents) {
      this.day = day;
    } else {
      this.day = null;
    }
  }

  createEvent(data: any) {
    if (data) {
      data.id = this.getId();
      const dateParts = data.date.split('-').map(part => parseInt(part, 10));

      const startTimeParts = data.start.split(':').map(part => parseInt(part, 10));
      const endTimeParts = data.end.split(':').map(part => parseInt(part, 10));

      const newEvent: CalendarEvent = {
        id: data.id,
        start: new Date(dateParts[0], dateParts[1] - 1, dateParts[2], startTimeParts[0], startTimeParts[1]),
        end: new Date(dateParts[0], dateParts[1] - 1, dateParts[2], endTimeParts[0], endTimeParts[1]),
        title: this.moduleTitle,
        color: {
          primary: '#46AE9B',
          secondary: '#46AE9B',
        },
      };

      this.events = [...this.events, newEvent];

      this.createESMTriggerRule(data);
    }
  }

  updateEvent(data: any) {
    const dateParts = data.date.split('-').map(part => parseInt(part, 10));

    const startTimeParts = data.start.split(':').map(part => parseInt(part, 10));
    const endTimeParts = data.end.split(':').map(part => parseInt(part, 10));

    const updatedEvent: CalendarEvent = {
      id: data.id,
      start: new Date(dateParts[0], dateParts[1] - 1, dateParts[2], startTimeParts[0], startTimeParts[1]),
      end: new Date(dateParts[0], dateParts[1] - 1, dateParts[2], endTimeParts[0], endTimeParts[1]),
      title: this.moduleTitle,
      color: {
        primary: '#46AE9B',
        secondary: '#46AE9B',
      },
    };

    const eventIndex = this.events.findIndex(event => event.id === data.id);
    if (eventIndex !== -1) {
      this.events = [
        ...this.events.slice(0, eventIndex),
        updatedEvent,
        ...this.events.slice(eventIndex + 1)
      ];
    }

    this.updateESMTriggerRule(data);
  }

  onParticipantStartDayChange() {
    if (this.isRelativeTriggerType) {
      this.esmTriggerRules.forEach(rule => {
        this.events.forEach(event => {
          const tempId = rule.id ? rule.id : rule.eventId;
          if (tempId === event.id) {
            const dateParts = this.offsetToDate(rule.daysOffset).split('-').map(part => parseInt(part, 10));

            const startTimeParts = rule.startTime.split(':').map(part => parseInt(part, 10));
            const endTimeParts = rule.endTime.split(':').map(part => parseInt(part, 10));

            const updatedEvent: CalendarEvent = {
              id: event.id,
              start: new Date(dateParts[0], dateParts[1] - 1, dateParts[2], startTimeParts[0], startTimeParts[1]),
              end: new Date(dateParts[0], dateParts[1] - 1, dateParts[2], endTimeParts[0], endTimeParts[1]),
              title: this.moduleTitle,
              color: {
                primary: '#46AE9B',
                secondary: '#46AE9B',
              },
            };

            const eventIndex = this.events.findIndex(tempEvent => tempEvent.id === event.id);
            if (eventIndex !== -1) {
              this.events = [
                ...this.events.slice(0, eventIndex),
                updatedEvent,
                ...this.events.slice(eventIndex + 1)
              ];
            }
          }
        });
      })
    }
  }

  createESMTriggerRule(data: any) {
    let newRule: ESMTriggerRule;
    if (this.isRelativeTriggerType) {
      newRule = {
        eventId: data.id,
        triggerType: this.triggerType,
        optionType: data.selectedOption,
        fixedDate: null,
        daysOffset: data.daysOffset,
        startTime: data.start,
        endTime: data.end,
        amount: data.amount,
        times: data.setTimes
      }
    } else {
      newRule = {
        eventId: data.id,
        triggerType: this.triggerType,
        optionType: data.selectedOption,
        fixedDate: data.date,
        daysOffset: null,
        startTime: data.start,
        endTime: data.end,
        amount: data.amount,
        times: data.setTimes
      }
    }

    this.esmTriggerRules = [...this.esmTriggerRules, newRule];
  }

  updateESMTriggerRule(data: any) {
    let updateRule: ESMTriggerRule;
    if (this.isRelativeTriggerType) {
      updateRule = {
        eventId: data.id,
        triggerType: this.triggerType,
        optionType: data.selectedOption,
        fixedDate: null,
        daysOffset: data.daysOffset,
        startTime: data.start,
        endTime: data.end,
        amount: data.amount,
        times: data.setTimes
      }
    } else {
      updateRule = {
        eventId: data.id,
        triggerType: this.triggerType,
        optionType: data.selectedOption,
        fixedDate: data.date,
        daysOffset: null,
        startTime: data.start,
        endTime: data.end,
        amount: data.amount,
        times: data.setTimes
      }
    }

    const ruleIndex = this.esmTriggerRules.findIndex(rule => rule.eventId === data.id);
    if (ruleIndex !== -1) {
      this.esmTriggerRules = [
        ...this.esmTriggerRules.slice(0, ruleIndex),
        updateRule,
        ...this.esmTriggerRules.slice(ruleIndex + 1)
      ];
    }
  }

  removeEvent(eventId: number) {
    this.events = this.events.filter(event => event.id !== eventId);
    this.removeESMTriggerRule(eventId);
  }

  removeESMTriggerRule(id: number) {
    this.esmTriggerRules = this.esmTriggerRules.filter(rule => rule.eventId !== id)
  }

  getId(): number {
    if (this.events.length === 0) {
      this.idCount = 0;
    } else {
      this.idCount = Number(this.events[this.events.length - 1].id);
    }
    return this.idCount + 1;
  }

  isStartDay(day: Date): boolean {
    const participantStartDate = new Date(this.participantStartDay);
    return day.getFullYear() === participantStartDate.getFullYear() &&
      day.getMonth() === participantStartDate.getMonth() &&
      day.getDate() === participantStartDate.getDate();
  }

  offsetToDate(offset: number): string {
    const date = new Date(this.participantStartDay);
    if (offset) {
      date.setDate(date.getDate() + offset);
    }
    return date.toISOString().split('T')[0];
  }

  getESMTriggerRules(): ESMTriggerRule[] {
    return this.esmTriggerRules;
  }
}
